import { FC } from "react";

import {
  useModalStack,
  PushableModalProps,
} from "@hl/shared-features/lib/features/modal";
import { Group, Title, ActionIcon, MantineTheme } from "@mantine/core";

import { Condition } from "apollo/graphql.generated";
import { ReactComponent as ArrowLeftIcon } from "assets/icons/arrow-left.svg";

import FindAttribute from "./FindAttribute";
import FindCollection from "./FindCollection";
import FindToken from "./FindToken";
import SetAllowlist from "./SetAllowlist";
import SetCurrency from "./SetCurrency";
import SetFarcasterState from "./SetFarcasterState";

export const useConditionModals = (
  addOrEditCondition: (
    condition: Condition,
    initialCondition?: Condition
  ) => void,
  showBackArrow = false,
  submitText?: string
) => {
  const { pushModal, popModal } = useModalStack();
  // Nested modals
  const nestedModalProps: PushableModalProps = {
    size: "xl",
    padding: 16,
  };

  // Factory to make condition modal handler callback functions
  const makeConditionModalHandler = (
    C: FC<{
      onSave: (condition: Condition) => void;
      initialCondition?: Condition;
      submitText?: string;
    }>,
    title: string,
    showBackArrow: boolean,
    hideDivider = false
  ) => {
    const customModalStyles = hideDivider
      ? {
          styles: (theme: MantineTheme) => ({
            header: {
              padding: `0 ${theme.spacing.sm}px 0 ${theme.spacing.md}px`,
              borderBottom: "none",
            },
          }),
        }
      : {};

    return ({
      initialCondition,
      title: customTitle,
      submitText: customButtonText,
      showBackButton: customShowBackButton,
    }: {
      initialCondition?: Condition;
      title?: string;
      submitText?: string;
      showBackButton?: boolean;
    } = {}) => {
      const shouldShowBackButton = customShowBackButton ?? showBackArrow;
      return pushModal(
        <C
          initialCondition={initialCondition}
          onSave={(condition) => {
            addOrEditCondition(condition, initialCondition);
            popModal();
          }}
          submitText={customButtonText || submitText}
        />,
        {
          ...nestedModalProps,
          title: (
            <Group spacing="xs">
              {shouldShowBackButton && (
                <ActionIcon onClick={popModal}>
                  <ArrowLeftIcon />
                </ActionIcon>
              )}
              <Title size={18}>{customTitle || title}</Title>
            </Group>
          ),
          ...customModalStyles,
        }
      );
    };
  };
  // without the factory, defining such a handler would look like this:
  // const openFindTokenModal = () => {
  //   pushModal(
  //     <FindToken
  //       onSave={(condition) => {
  //         addCondition(condition);
  //         popModal();
  //       }}
  //     />,
  //     { ...nestedModalProps, title: <Title size={16}>Find Token</Title> }
  //   );
  // };

  const openFindTokenModal = makeConditionModalHandler(
    FindToken,
    "Find token",
    showBackArrow,
    true
  );
  const openFindCollectionModal = makeConditionModalHandler(
    FindCollection,
    "Find collection",
    showBackArrow,
    true
  );
  const openSetCurrencyModal = makeConditionModalHandler(
    SetCurrency,
    "Set cryptocurrency",
    showBackArrow,
    true
  );
  const openFindAttributeModal = makeConditionModalHandler(
    FindAttribute,
    "Choose token attribute",
    showBackArrow,
    true
  );
  const openSetAllowlistModal = makeConditionModalHandler(
    SetAllowlist,
    "Set allowlist",
    showBackArrow,
    true
  );

  const openFarcasterModal = makeConditionModalHandler(
    SetFarcasterState,
    "Set Farcaster condition",
    showBackArrow,
    true
  );

  return {
    openFindTokenModal,
    openFindCollectionModal,
    openSetCurrencyModal,
    openFindAttributeModal,
    openSetAllowlistModal,
    openFarcasterModal,
  };
};
